import { Injectable, inject, signal, computed, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';

const TENANT_ID_STORAGE_KEY = 'pwa-tenant-id';

/**
 * Injection token for the window.location object.
 * Allows overriding in tests.
 */
export const WINDOW_LOCATION = new InjectionToken<Location>('WINDOW_LOCATION', {
  providedIn: 'root',
  factory: () => window.location,
});

export interface TenantInfo {
  tenant_id: string;
  company_name?: string;
  name?: string;
}

@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly location = inject(WINDOW_LOCATION);

  /** The resolved tenant ID (null if not yet resolved) */
  readonly tenantId = signal<string | null>(null);

  /** The tenant's company name fetched from the backend */
  readonly tenantName = signal<string | null>(null);

  /** Whether the tenant context failed to resolve (no query param, no localStorage) */
  readonly hasError = signal(false);

  /** Loading state while fetching tenant info */
  readonly isLoading = signal(false);

  /** Convenience computed: whether a valid tenant is resolved */
  readonly isResolved = computed(() => this.tenantId() !== null && !this.hasError());

  /**
   * Resolve the tenant from the URL query parameter or localStorage.
   * Should be called early during app initialization (e.g., in APP_INITIALIZER or root component).
   *
   * Uses multiple extraction strategies to handle edge cases where
   * window.location.search may not contain the query params (e.g., mobile
   * PWA opened from QR code scanner, service worker cached shell).
   */
  initialize(): void {
    const tenantIdFromUrl = this.extractTenantIdFromUrl();

    if (tenantIdFromUrl) {
      this.setTenantId(tenantIdFromUrl);
      this.fetchTenantInfo(tenantIdFromUrl);
      return;
    }

    const storedTenantId = localStorage.getItem(TENANT_ID_STORAGE_KEY);
    if (storedTenantId) {
      this.tenantId.set(storedTenantId);
      this.fetchTenantInfo(storedTenantId);
      return;
    }

    // No tenant ID available — error state
    this.hasError.set(true);
  }

  /**
   * Attempt to re-resolve the tenant from the current URL or a provided ID.
   * Called as a fallback when the initial APP_INITIALIZER resolution
   * failed (e.g., on mobile where query params may not be available
   * during bootstrap but become available after navigation completes).
   *
   * @param fallbackTenantId - Optional tenant ID to use if URL extraction fails
   * Returns true if tenant was successfully resolved.
   */
  retryResolveFromUrl(fallbackTenantId?: string): boolean {
    if (this.tenantId()) {
      return true; // Already resolved
    }

    const tenantIdFromUrl = this.extractTenantIdFromUrl();
    const resolvedId = tenantIdFromUrl || fallbackTenantId;

    if (resolvedId) {
      this.setTenantId(resolvedId);
      this.hasError.set(false);
      this.fetchTenantInfo(resolvedId);
      return true;
    }

    return false;
  }

  /**
   * Get the current tenant ID or throw if not resolved.
   */
  getTenantIdOrThrow(): string {
    const id = this.tenantId();
    if (!id) {
      throw new Error('Tenant context not resolved');
    }
    return id;
  }

  /**
   * Extract the `tenantid` query parameter from the current URL.
   * Uses multiple strategies to handle mobile/PWA edge cases:
   * 1. window.location.search (standard)
   * 2. window.location.href parsing (fallback for cases where .search is empty)
   */
  private extractTenantIdFromUrl(): string | null {
    // Strategy 1: Standard query string parsing
    const params = new URLSearchParams(this.location.search);
    const fromSearch = params.get('tenantid');
    if (fromSearch) {
      return fromSearch;
    }

    // Strategy 2: Parse from the full href (handles edge cases where
    // mobile browsers or PWA shells may not populate .search correctly)
    const href = this.location.href;
    const qIndex = href.indexOf('?');
    if (qIndex !== -1) {
      const hrefParams = new URLSearchParams(href.substring(qIndex));
      const fromHref = hrefParams.get('tenantid');
      if (fromHref) {
        return fromHref;
      }
    }

    return null;
  }

  /**
   * Store the tenant ID in both the signal and localStorage.
   */
  private setTenantId(tenantId: string): void {
    this.tenantId.set(tenantId);
    localStorage.setItem(TENANT_ID_STORAGE_KEY, tenantId);
  }

  /**
   * Fetch tenant details (company_name) from the backend.
   */
  private fetchTenantInfo(tenantId: string): void {
    this.isLoading.set(true);

    this.api.get<{ ok: boolean; data?: TenantInfo; error?: string }>(
      `/tenants/${tenantId}`
    ).subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          this.tenantName.set(response.data.company_name || response.data.name || null);
        } else if (response.ok && (response as any).company_name) {
          this.tenantName.set((response as any).company_name);
        } else if (response.ok && (response as any).name) {
          this.tenantName.set((response as any).name);
        } else {
          // Tenant not found or error — keep tenant ID but clear name
          this.tenantName.set(null);
        }
        this.isLoading.set(false);
      },
      error: () => {
        // Network error — keep tenant ID, name unavailable
        this.tenantName.set(null);
        this.isLoading.set(false);
      },
    });
  }
}
